












































































import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
import SelectTrainerListRow from './SelectTrainerListRow.vue';
import { CourseTypeOption } from '@/store/modules/jtms/course/course.types';
import {
  SelectedTrainer,
  TrainerResponseDTO
} from '@/store/modules/jtms/trainer/trainer.types';
import { GroupLevelAttributeValue } from '@/jbi-shared/types/jaas-group-level-attributes.types';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import { deepCopy } from '@/jbi-shared/util/deep-copy.util';
import { CourseModuleComponentItem } from '@/store/modules/jtms/course-module/course-module.types';

@Component({
  components: {
    SelectTrainerListRow
  }
})
export default class TrainerDropdown extends Vue {
  @Prop() attributeValue!: GroupLevelAttributeValue;
  @Prop({ default: false }) isSettings!: boolean;
  @Prop({ default: false }) hasFieldErrorMessage!: boolean;
  @Prop({ default: false }) disabled!: boolean;
  @Prop() moduleIndex!: number;
  @Prop({ type: Array })
  readonly value!: TrainerResponseDTO[];

  @Action('trainer/getTrainers')
  getAllTrainers!: () => void;

  @State((state: RootState) => state.trainer.trainers)
  trainers!: TrainerResponseDTO[];

  @State(
    (state: RootState) => state.course.courseCreationState.selectedCourseType
  )
  selectedCourseType!: CourseTypeOption;

  @State((state: RootState) => state.course.courseCreationState.hasModules)
  hasModules!: boolean;

  @State(
    (state: RootState) =>
      state.courseModule.courseModulesTabState.activeCourseModules
  )
  activeCourseModules!: CourseModuleComponentItem[];

  searchTerm: string = '';
  clonedAllTrainers: TrainerResponseDTO[] = [];

  get filteredTrainers() {
    return this.clonedAllTrainers.filter(
      (trainer: TrainerResponseDTO) =>
        trainer
          .firstName!.toLowerCase()
          .indexOf(this.searchTerm.toLowerCase()) >= 0 ||
        trainer
          .lastName!.toLowerCase()
          .indexOf(this.searchTerm.toLowerCase()) >= 0
    );
  }

  created() {
    if (!this.trainers) {
      this.getAllTrainers();
    }
  }

  mounted() {
    if (this.trainers && this.trainers.length > 0) {
      this.assignAllTrainers();
    }
  }

  getFullName(firstName?: string, lastName?: string) {
    return `${firstName} ${lastName}`;
  }

  // This method is for removing tags from the trigger
  removeTrainer(trainerToRemove: TrainerResponseDTO): void {
    const newValue = this.value.filter((t) => t.id !== trainerToRemove.id);
    this.emitTrainerArrayEvent(newValue);
  }

  // Keeping this method in case Buefy's v-model on b-dropdown needs it,
  // Else it does the funny when handling props
  onValueChanged(newValue: TrainerResponseDTO[]): void {
    this.emitTrainerArrayEvent(newValue);
  }

  assignAllTrainers() {
    this.clonedAllTrainers = deepCopy(this.trainers);
    // Ensure trainers are filtered based on accredition upon dropdown component load
    if (this.selectedCourseType) {
      this.filterTrainersByCourseTypeAccreditation();
    }

    // Assign data from database if any if initialised from GLAs
    if (this.isSettings && this.attributeValue.value) {
      const trainerIds: number[] = (this.attributeValue.value as string[]).map(
        (val) => {
          const [name, id] = val.split('.');
          return parseInt(id, 10);
        }
      );

      const initialValue = this.trainers.filter((trainers) =>
        trainerIds.some((ids) => ids === trainers.id)
      );

      this.emitTrainerArrayEvent(initialValue);
    }

    // Assign data from state if has course modules
    if (!this.isSettings && this.hasModules && this.activeCourseModules) {
      const currentModule = this.activeCourseModules[this.moduleIndex];
      const currentModuleTrainerIds = currentModule.trainers?.map(
        (trainer) => trainer.id
      );

      if (currentModuleTrainerIds!.length === 0) {
        // Do nothing if there are no existing values in state
        return;
      }

      const initialValue = this.trainers.filter((trainers) =>
        currentModuleTrainerIds!.some((ids) => ids === trainers.id)
      );

      this.emitTrainerArrayEvent(initialValue);
    }
  }

  handleTrainerSelection(selectedTrainer: SelectedTrainer) {
    const { selected, trainer } = selectedTrainer;

    // Create a new array to maintain reactivity
    let clonedSelectedTrainerArray = [...this.value];

    if (selected) {
      // Add trainer if it's not already in the list
      if (!clonedSelectedTrainerArray.some((t) => t.id === trainer.id)) {
        clonedSelectedTrainerArray.push(trainer);
      }
    } else {
      // Remove trainer from the list
      clonedSelectedTrainerArray = clonedSelectedTrainerArray.filter(
        (t) => t.id !== trainer.id
      );
    }

    this.emitTrainerArrayEvent(clonedSelectedTrainerArray);
  }

  filterTrainersByCourseTypeAccreditation() {
    this.clonedAllTrainers = this.trainers.filter(
      (trainer) =>
        trainer.accreditation === this.selectedCourseType.name ||
        trainer.accreditation === null
    );
  }

  /**
   * Whenever course types are changed, the trainer list in the dropdown should be
   * updated to reflect trainers based on accreditation since course type syncs with
   * accreditation types
   */
  @Watch('selectedCourseType')
  onCourseTypeChange() {
    this.filterTrainersByCourseTypeAccreditation();
  }

  /**
   * This watcher is necessary to ensure `clonedAllTrainers` gets reassigned
   * upon course module radio selection change.
   *
   * The `allTrainers` prop will only be passed down in Course Attributes Tab
   * This is because this component is reused by a dynamically rendering
   * GroupLevelAttributeTrainerField, so whenever it is disabled then enabled,
   * the cloned trainers has to be reset.
   */
  @Watch('trainers')
  triggerReassign() {
    this.assignAllTrainers();
  }

  @Emit('input')
  emitTrainerArrayEvent(newValue: TrainerResponseDTO[]) {
    return newValue;
  }
}
